import { rigListQuerySchema, type Page, type RigListItem, type RigListQuery } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import { listRigs } from '../units.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type UnitsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<RigListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: RigListQuery) => toListParams(query).toString();

function toErrorState(err: unknown): UnitsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of units the URL's list query asks for, re-fetched whenever that query changes or a coupling changes. */
export function useUnitList() {
  const list = useListQuery(rigListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<UnitsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listRigs(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', page: await listRigs(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);
  return { state, list, isRefreshing, reload };
}
